import type { TeamMember } from "@brookbush/types";

import Image from "next/image";
import { useState } from "react";
import useSWR, { type SWRResponse } from "swr";

import Header from "~/components/Header";
import SectionContainer from "~/components/SectionContainer";

const AboutTeam = () => {
	const { data: members } = useSWR<TeamMember[]>(
		`/company-info/team`,
	) as SWRResponse<TeamMember[]> & {
		data: TeamMember[];
	};

	return (
		<SectionContainer
			className="border-b"
			id="team"
			aria-labelledby="team-title"
		>
			<div className="mx-auto flex max-w-screen-lg flex-col gap-12">
				<Header
					id="team-title"
					title="Our Team"
					description="People on the mission to make education accessible to everyone."
				/>
				<ul className="mx-auto grid phablet:grid-cols-2 gap-8">
					{members
						.sort((a, b) => {
							if (a.name === "Brent Brookbush") return -1;
							if (b.name === "Brent Brookbush") return 1;
							if (a.credentials && b.credentials) {
								return b.credentials.length - a.credentials.length;
							}
							return a.name.localeCompare(b.name);
						})
						.map((person) => (
							<li key={person._id}>
								<div className="flex h-full flex-col gap-4 rounded-xl border px-6 py-8 shadow-slate-200/20 shadow-xl dark:shadow-slate-900/50">
									<div className="flex items-center gap-4">
										<div className="relative h-20 phablet:h-36 phablet:w-36 w-20 flex-shrink-0 overflow-hidden rounded-full shadow-gray-800/10 shadow-xl dark:shadow-slate-900/40">
											<Image
												fill
												src={person.avatar}
												alt={`${person.name} - ${person.profession}`}
												className="object-cover"
											/>
										</div>
										<div className="flex flex-col gap-2 font-medium text-lg leading-6">
											<h3 className="text-xl">{person.name}</h3>
											<p className="bg-gradient-to-r from-primary-600 to-primary-900 bg-clip-text font-medium text-transparent">
												{person.profession}
											</p>
										</div>
									</div>
									<div className="w-full">
										<TabSection
											narrative={person.narrative}
											credentials={person.credentials}
										/>
									</div>
								</div>
							</li>
						))}
				</ul>
			</div>
		</SectionContainer>
	);
};

const TabSection: React.FC<{
	narrative: string;
	credentials: { credential: string; institution: string }[] | undefined;
}> = ({ narrative, credentials }) => {
	const [activeTab, setActiveTab] = useState<"narrative" | "credentials">(
		"narrative",
	);
	const [isExpanded, setIsExpanded] = useState(false);

	const truncatedNarrative = narrative.split("\n").slice(0, 2).join("\n");

	return (
		<div className="w-full">
			<div className="flex border-gray-200 border-b dark:border-gray-700">
				<button
					onClick={() => setActiveTab("narrative")}
					className={`px-4 py-2 font-medium text-sm ${
						activeTab === "narrative"
							? "border-primary-600 border-b-2 text-primary-600"
							: "text-gray-500 dark:text-gray-400"
					}`}
				>
					About
				</button>
				<button
					onClick={() => setActiveTab("credentials")}
					className={`ml-4 px-4 py-2 font-medium text-sm ${
						activeTab === "credentials"
							? "border-primary-600 border-b-2 text-primary-600"
							: "text-gray-500 dark:text-gray-400"
					}`}
				>
					Credentials
				</button>
			</div>
			<div className="mt-4">
				{activeTab === "narrative" ? (
					<div>
						<p className="whitespace-pre-line text-left text-sm">
							{isExpanded ? narrative : truncatedNarrative}
						</p>
						<button
							onClick={() => setIsExpanded(!isExpanded)}
							className="mt-2 font-medium text-primary-600 text-sm"
						>
							{isExpanded ? "Read Less" : "Read More"}
						</button>
					</div>
				) : (
					<ul className="space-y-2">
						{credentials?.map((credential, index) => (
							<li
								className="text-left text-black text-sm leading-normal dark:text-gray-100"
								key={index}
							>
								<span className="font-semibold">{credential.credential}</span> -{" "}
								<span className="text-gray-600 text-sm dark:text-gray-400">
									{credential.institution}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};

export default AboutTeam;
